<!--
// app/components/pages/region-matches-match-homeTeam-vs-awayTeam/MatchMainJumbotron.vue
-->
<script setup lang="ts">
import type { SocialMedia } from "~/src/region/domain/models/Region";
import type { Match } from "~/src/match/domain/models/Match";
import type { Phase } from "~/src/season-cluster/domain/models/02.Phase";
import matchService from "~/src/match/domain/services/matchService";
import matchUiService from "~/src/match/infrastructure/ui-services/matchUiService";
import { usePhases } from "~/app/composables/season/phase";
import { useCompetitionStore } from "~/app/stores/competitionStore";
import { useMatches } from "~/app/composables/season/matches";
import { useRegions } from "~/app/composables/regions";
import { isFutureDate, setAliasUrl } from "~/src/Shared/utils";
import MatchResultsJumbotron from "~/app/components/pages/region-matches-match-homeTeam-vs-awayTeam/MatchResultsJumbotron.vue";
import MatchEvents from "~/app/components/pages/region-matches-match-homeTeam-vs-awayTeam/MatchEvents.vue";
import TwitchIcon from "~/app/components/Shared/icons/TwitchIcon.vue";
import TicketIcon from "~/app/components/Shared/icons/TicketIcon.vue";

type Props = {
  match: Match;
};

const props = defineProps<Props>();

const { getPhase } = usePhases();
const { locale } = useI18n();
const { region } = useCompetitionStore();
const { regionTimeZone, isWorldCup, isNations } = useRegions();
const { transformStatus } = matchUiService();
const { setMatchTimeZone, showTime, showDate } = useMatches();
const { matchIsLive } = matchService();

const regionSocialProfiles: SocialMedia[] = region?.social || [];

const liveButtonUrl: string | undefined =
  (isWorldCup || isNations) && locale.value === "es"
    ? "https://www.twitch.tv/kingsleague"
    : regionSocialProfiles.find(s => s.name === "twitch")?.url || "";

const phase = ref<Phase>();
const groupName = ref<string | undefined>();

const stadiumBgPlaceholder = "/img/placeholder/stadium_bg_placeholder.jpg";

const forcedTimezone: boolean = !!props.match.metaInformation && !!props.match.metaInformation.match_timezone;

const timeZoneInfo = setMatchTimeZone(
  forcedTimezone ? "appendix" : "country",
  forcedTimezone ? props.match.metaInformation?.match_timezone : props.match.stadium?.countryId
);
const computedTimeZone = timeZoneInfo?.timezone || regionTimeZone;
const timeZoneAppendix = timeZoneInfo?.appendix || "";

const showTimeComputed = showTime(props.match.date);
const showDateComputed = showDate(props.match.date);

onMounted(async () => {
  await nextTick();
  try {
    const phaseId = props.match?.phaseId;
    if (phaseId) {
      phase.value = await getPhase(phaseId);
      groupName.value = phase.value?.groups.find(group => group.id === props.match.groupId).name || "";
    }
  } catch (e) {
    console.error(e);
  }
});
</script>

<template>
  <div
    v-if="match"
    class="kql-match-header"
    :style="[
      match.id === 1900
        ? ''
        : match.stadium?.image?.url
          ? `background-image: linear-gradient(0deg, rgba(0, 0, 0, 0.60) 0%, rgba(0, 0, 0, 0.60) 100%), url('${match.stadium?.image.url}');`
          : `background-image: linear-gradient(0deg, rgba(0, 0, 0, 0.60) 0%, rgba(0, 0, 0, 0.60) 100%), url('${stadiumBgPlaceholder}')`,
      'background-position: center; background-size: cover; background-repeat: no-repeat;',
    ]"
  >
    <NuxtImg
      v-if="match?.season?.image?.url && match.id !== 1900"
      :src="setAliasUrl(match.season.image?.url)"
      :alt="match.season.name"
      class="object-contain object-top w-40"
      fit="outside"
      loading="lazy"
      width="160"
      height="105"
    />
    <div v-if="phase && match.id !== 1900" class="flex">
      <p class="text-xs text-primary font-medium">{{ phase.name }}</p>
      <p
        v-if="
          phase.groups && phase.groups.length > 1 && phase.groups.find(group => group.id === match?.groupId)
        "
        class="text-xs text-primary font-medium"
      >
        &nbsp-&nbsp{{ groupName?.toLowerCase() === "challenger" ? "" : $t("matches.turn.group") }}
        {{ groupName }}
      </p>
    </div>
    <div v-if="phase && match.id !== 1900">
      <p
        v-if="match.turnId && phase.groups"
        class="text-xs text-primary font-medium px-3 py-2 border rounded-md border-primary"
      >
        {{
          phase.groups
            .find(group => group.id === match?.groupId)
            ?.turns.find(turn => turn.id === match?.turnId)?.name
        }}
      </p>
    </div>
    <match-results-jumbotron :match="match" />
    <div class="text-center">
      <p class="text-sm lg:text-base">
        <span v-if="showDateComputed">
          <NuxtTime
            v-if="showDateComputed"
            :datetime="match.date"
            :locale="locale"
            :time-zone="computedTimeZone"
            date-style="long"
          />
        </span>
        <span v-if="showTimeComputed"
          >,
          <NuxtTime
            :datetime="match.date"
            :locale="locale"
            :time-zone="computedTimeZone"
            hour="2-digit"
            minute="2-digit"
          />
          <span v-if="timeZoneAppendix" class="ps-1">{{ timeZoneAppendix }}</span>
        </span>
      </p>
      <p v-if="match.status" class="font-bold capitalize">
        {{ $t(transformStatus(match.status)) }}
        <span v-if="matchIsLive(match)" class="font-normal">({{ match.currentMinute }}')</span>
      </p>
    </div>
    <div class="flex flex-wrap justify-center items-center gap-x-4 gap-y-2">
      <nuxt-link
        v-if="match.metaInformation?.youtube_url_extended && !matchIsLive(match)"
        :to="match.metaInformation.youtube_url_extended"
        class="px-3 py-2 text-primary rounded-md bg-black text-xs"
        target="_blank"
      >
        <svg
          class="youtube-logo inline-block me-2"
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="13"
          viewBox="0 0 12 13"
          fill="none"
        >
          <path
            d="M11.27 3.71061C11.2078 3.47659 11.0852 3.26301 10.9146 3.09124C10.7439 2.91948 10.5311 2.79555 10.2975 2.73186C9.4375 2.50061 6 2.50061 6 2.50061C6 2.50061 2.5625 2.50061 1.7025 2.73186C1.46888 2.79555 1.25609 2.91948 1.08542 3.09124C0.914753 3.26301 0.79219 3.47659 0.73 3.71061C0.5 4.57436 0.5 6.37561 0.5 6.37561C0.5 6.37561 0.5 8.17686 0.73 9.04061C0.79219 9.27463 0.914753 9.48821 1.08542 9.65998C1.25609 9.83174 1.46888 9.95567 1.7025 10.0194C2.5625 10.2506 6 10.2506 6 10.2506C6 10.2506 9.4375 10.2506 10.2975 10.0194C10.5311 9.95567 10.7439 9.83174 10.9146 9.65998C11.0852 9.48821 11.2078 9.27463 11.27 9.04061C11.5 8.17686 11.5 6.37561 11.5 6.37561C11.5 6.37561 11.5 4.57436 11.27 3.71061Z"
            fill="#FF0302"
          />
          <path d="M4.87501 8.01125V4.74L7.75001 6.37563L4.87501 8.01125Z" fill="white" />
        </svg>
        {{ $t("match.jumbotron.watchMatch") }}
      </nuxt-link>
      <nuxt-link
        v-if="match.metaInformation?.youtube_url && !matchIsLive(match)"
        :to="match.metaInformation.youtube_url"
        class="px-3 py-2 text-primary rounded-md bg-black text-xs"
        target="_blank"
      >
        <svg
          class="youtube-logo inline-block me-2"
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="13"
          viewBox="0 0 12 13"
          fill="none"
        >
          <path
            d="M11.27 3.71061C11.2078 3.47659 11.0852 3.26301 10.9146 3.09124C10.7439 2.91948 10.5311 2.79555 10.2975 2.73186C9.4375 2.50061 6 2.50061 6 2.50061C6 2.50061 2.5625 2.50061 1.7025 2.73186C1.46888 2.79555 1.25609 2.91948 1.08542 3.09124C0.914753 3.26301 0.79219 3.47659 0.73 3.71061C0.5 4.57436 0.5 6.37561 0.5 6.37561C0.5 6.37561 0.5 8.17686 0.73 9.04061C0.79219 9.27463 0.914753 9.48821 1.08542 9.65998C1.25609 9.83174 1.46888 9.95567 1.7025 10.0194C2.5625 10.2506 6 10.2506 6 10.2506C6 10.2506 9.4375 10.2506 10.2975 10.0194C10.5311 9.95567 10.7439 9.83174 10.9146 9.65998C11.0852 9.48821 11.2078 9.27463 11.27 9.04061C11.5 8.17686 11.5 6.37561 11.5 6.37561C11.5 6.37561 11.5 4.57436 11.27 3.71061Z"
            fill="#FF0302"
          />
          <path d="M4.87501 8.01125V4.74L7.75001 6.37563L4.87501 8.01125Z" fill="white" />
        </svg>
        {{ $t("match.jumbotron.watchHighlights") }}
      </nuxt-link>
      <nuxt-link
        v-if="matchIsLive(match)"
        :to="liveButtonUrl"
        class="px-3 py-2 text-primary rounded-md bg-black text-xs"
        target="_blank"
        ><span class="flex gap-2 items-center">
          <span class="relative flex h-2 w-2">
            <span
              class="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-600 opacity-75"
            />
            <span class="relative inline-flex rounded-full h-2 w-2 bg-red-600" />
          </span>
          <span class="flex items-center gap-1"> <twitch-icon width="10" />{{ $t("header.liveBtn") }}</span>
        </span></nuxt-link
      >
    </div>

    <p
      v-if="(match.id !== 1900 && match.stadium) || (match.stadium && match.id === 1900 && showTimeComputed)"
      class="font-bold capitalize"
    >
      {{ match.stadium?.name }}
    </p>
    <nuxt-link
      v-if="match.metaInformation && match.metaInformation.tickets_url && isFutureDate(match.date)"
      :to="match.metaInformation?.tickets_url"
      class="btn btn-emphasis !px-3 !py-2 text-sm"
      target="_blank"
    >
      <span class="flex items-center gap-1"> <ticket-icon width="10" />{{ $t("header.tickets") }}</span>
    </nuxt-link>
  </div>
  <div class="kql-w-container">
    <match-events v-if="match" :match="match" />
  </div>
</template>

<style scoped>
.kql-match-header {
  @apply bg-center bg-cover w-full px-9 py-14 flex items-center justify-center flex-col gap-4 relative;
}
</style>
